// ============================================================
// vialRecompute - recompute a protocol component's vial-derived
// fields when it gets pointed at a DIFFERENT physical vial.
//
// Why this exists (vial lifecycle review 2026-07-08, P0-1): three
// flows repoint component.inventoryId at a new vial - refill,
// auto-switch promote, and "link a vial". Historically all three
// only swapped the pointer and kept the component's OLD
// concentrationMcgPerUnit, so if the new vial was mixed differently
// (5mg/3mL instead of 5mg/2mL) the app kept instructing the old
// draw units - a silent wrong dose. Every repoint path MUST run
// through this helper so the draw instruction always matches the
// vial actually in use.
//
//   old vial            new vial             component after
//   5mg / 2mL           10mg / 2mL           concentration doubles,
//   25 mcg/unit   -->   50 mcg/unit    -->   draw units halve,
//   250mcg = 10u        250mcg = 5u          dose (mcg) unchanged
//
// Design contract (review decision 1A / C7): ALL dose math lives in
// TypeScript - deriveMixtureFields and calculateDosesPerVial are the
// sole derivation writers. This module composes them; it never
// invents new math, and the refill RPC never re-derives any of it.
// ============================================================

import type { InventoryItem, ProtocolComponent } from '../data/types';
import {
  calculateConcentration,
  deriveMixtureFields,
  formatUnits,
  unitsForDose,
} from './calculations';
import { getCurrentPhase } from './protocol';

/**
 * Plain-English "what changed" note for the AI summary opener when a vial
 * change moves the draw. No math, no mcg/mg - the model already has the new
 * units. Same wording used by the refill flow and the auto-promote path so
 * the explanation stays consistent everywhere.
 */
export function buildDrawChangeNote(oldUnits: number, newUnits: number): string {
  const strength = newUnits < oldUnits ? 'stronger' : 'weaker';
  return `The amount to draw changed from ${formatUnits(oldUnits)} units to ${formatUnits(newUnits)} units because the new vial is mixed a little ${strength}. The dose itself is exactly the same as before.`;
}

export interface DrawChange {
  componentId: string;
  peptideName: string;
  /** Instructed draw units BEFORE the repoint (0 when previously uncomputable). */
  oldUnits: number;
  /** Instructed draw units AFTER the repoint. */
  newUnits: number;
}

export interface VialRecomputeResult {
  /** Same order as the input; untouched components are the same object. */
  components: ProtocolComponent[];
  /** True when any field actually changed (callers can skip the write). */
  changed: boolean;
  /** One entry per component whose instructed draw units changed. */
  drawChanges: DrawChange[];
}

/**
 * Recompute vial-derived fields (vialSizeMg, bacWaterMl,
 * concentrationMcgPerUnit, drawUnits for mixes) on the components
 * being linked to `vial`.
 *
 * `components` must be ONLY the components that will point at this
 * vial (the caller filters by inventoryId / mix group). Singles and
 * mixes are distinguished by the VIAL: a vial carrying a mixRecipe
 * is a combined vial and recomputes through deriveMixtureFields with
 * the LEAD component's current-phase dose preserved exactly (the
 * other peptides follow the recipe ratio - that is what a combined
 * draw physically does).
 *
 * Returns null when the vial cannot support dose math (unmixed /
 * zero-size / mix vial without a recipe). Callers MUST treat null as
 * "do not write anything" - persisting a 0 concentration would poison
 * every downstream units calculation. (Review decision: refuse,
 * never guess.)
 */
export function recomputeComponentsForVial(
  components: ProtocolComponent[],
  vial: InventoryItem,
): VialRecomputeResult | null {
  if (components.length === 0) return { components, changed: false, drawChanges: [] };
  if (!(vial.vialSizeMg > 0) || !(vial.bacWaterMl > 0)) return null;

  const isMixVial = !!vial.mixGroupId || (vial.mixRecipe?.length ?? 0) > 0;

  if (!isMixVial) {
    // A single vial can only back single components. A mix component
    // pointed at a single vial is a caller bug (9A: mixes never match
    // singles) - refuse rather than compute nonsense.
    if (components.some((c) => c.mixGroupId)) return null;

    const concentration = calculateConcentration(vial.vialSizeMg, vial.bacWaterMl);
    if (!(concentration > 0)) return null;

    let changed = false;
    const drawChanges: DrawChange[] = [];
    const next = components.map((comp) => {
      if (
        comp.vialSizeMg === vial.vialSizeMg &&
        comp.bacWaterMl === vial.bacWaterMl &&
        comp.concentrationMcgPerUnit === concentration
      ) {
        return comp;
      }
      changed = true;
      const doseMcg = getCurrentPhase(comp).doseMcg;
      const oldUnits = comp.concentrationMcgPerUnit > 0
        ? unitsForDose(doseMcg, comp.concentrationMcgPerUnit)
        : 0;
      const newUnits = unitsForDose(doseMcg, concentration);
      if (oldUnits !== newUnits) {
        drawChanges.push({ componentId: comp.id, peptideName: comp.peptideName, oldUnits, newUnits });
      }
      return {
        ...comp,
        vialSizeMg: vial.vialSizeMg,
        bacWaterMl: vial.bacWaterMl,
        concentrationMcgPerUnit: concentration,
      };
    });
    return { components: next, changed, drawChanges };
  }

  // ---- Combined vial ----
  const recipe = vial.mixRecipe ?? [];
  if (recipe.length < 2) return null;

  // Preserve the LEAD component's current-phase dose exactly; derive
  // the shared draw from it (mirrors the wizard's perPeptide dose
  // mode in mixDrawUnits). Everything else follows the recipe ratio.
  const lead = components[0];
  const leadEntry = recipe.find((r) => r.peptideId === lead.peptideId);
  if (!leadEntry || !(leadEntry.amountMg > 0)) return null;

  const leadDoseMcg = getCurrentPhase(lead).doseMcg;
  if (!(leadDoseMcg > 0)) return null;

  const leadConcentration = calculateConcentration(leadEntry.amountMg, vial.bacWaterMl);
  const drawUnits = unitsForDose(leadDoseMcg, leadConcentration);
  if (!(drawUnits > 0)) return null;

  const derived = deriveMixtureFields(recipe, vial.bacWaterMl, drawUnits, lead.syringeType);
  if (!(derived.concentrationMcgPerUnit > 0)) return null;

  // A component whose peptide is missing from the recipe cannot be
  // recomputed - refuse the whole batch (a partial recompute would
  // leave the group internally inconsistent).
  const anyMissing = components.some(
    (comp) => !recipe.some((r) => r.peptideId === comp.peptideId),
  );
  if (anyMissing) return null;

  let changed = false;
  const drawChanges: DrawChange[] = [];
  const next = components.map((comp) => {
    const entry = recipe.find((r) => r.peptideId === comp.peptideId)!;
    const part = derived.perComponent.find((p) => p.peptideId === comp.peptideId)!;
    const oldUnits = comp.drawUnits ?? (
      comp.concentrationMcgPerUnit > 0
        ? unitsForDose(getCurrentPhase(comp).doseMcg, comp.concentrationMcgPerUnit)
        : 0
    );
    if (oldUnits !== drawUnits) {
      drawChanges.push({ componentId: comp.id, peptideName: comp.peptideName, oldUnits, newUnits: drawUnits });
    }
    changed = true;
    const phase = getCurrentPhase(comp);
    return {
      ...comp,
      vialSizeMg: entry.amountMg,
      bacWaterMl: vial.bacWaterMl,
      concentrationMcgPerUnit: derived.concentrationMcgPerUnit,
      drawUnits,
      // True delivered per-peptide dose under the (possibly changed)
      // recipe ratio. The lead's dose is preserved exactly by
      // construction; the others track their share of the draw.
      phases: comp.phases.map((p) => (p.id === phase.id ? { ...p, doseMcg: part.doseMcg } : p)),
    };
  });

  return { components: next, changed, drawChanges };
}

/**
 * Pick which backup vial (if any) an emptied vial may silently
 * promote to. Pure decision logic extracted from
 * maybeAutoSwitchEmptyVial so the policy is unit-testable.
 *
 * Policy (review decisions P1-4 / 4A+ / 9A):
 * - Sealed vials (no BAC water yet) are NEVER silently promoted -
 *   the user must go through Start Using so the 28-day clock and
 *   concentration are real. They surface as `sealed` instead.
 * - Mix vials never participate: a mix's peptide_id is just its lead
 *   peptide, so peptide-keyed matching would pair a Wolverine vial
 *   with a plain BPC-157 backup (or vice versa). Callers route
 *   emptied mixes to the Refill Combined Vial flow.
 * - Exactly one reconstituted single backup -> promote it.
 * - Two or more -> never silently pick; the caller prompts.
 */
export function selectPromotableBackup(backups: InventoryItem[]): {
  kind: 'none' | 'promote' | 'multiple' | 'sealed';
  vial?: InventoryItem;
  candidates?: InventoryItem[];
} {
  const singles = backups.filter((b) => !b.mixGroupId && (b.mixRecipe?.length ?? 0) === 0);
  const usable = singles.filter((b) => b.bacWaterMl > 0);
  if (usable.length === 1) return { kind: 'promote', vial: usable[0] };
  if (usable.length > 1) return { kind: 'multiple', candidates: usable };
  if (singles.length > 0) return { kind: 'sealed', vial: singles[0] };
  return { kind: 'none' };
}
